import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import "regenerator-runtime/runtime.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.error.cause.js";
import "core-js/modules/es.array.push.js";
import "core-js/modules/es.array.unshift.js";
import { DEBUG_BUILD } from '../debug-build.js';
import { envelopeContainsItemType } from '../utils-hoist/envelope.js';
import { logger } from '../utils-hoist/logger.js';
import { parseRetryAfterHeader } from '../utils-hoist/ratelimit.js';
var MIN_DELAY = 100; // 100 ms
var START_DELAY = 5000; // 5 seconds
var MAX_DELAY = 3.6e6; // 1 hour

/**
 * Wraps a transport and stores and retries events when they fail to send.
 *
 * @param createTransport The transport to wrap.
 */
function makeOfflineTransport(createTransport) {
  function log() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    DEBUG_BUILD && logger.info.apply(logger, ['[Offline]:'].concat(args));
  }
  return function (options) {
    var transport = createTransport(options);
    if (!options.createStore) {
      throw new Error('No `createStore` function was provided');
    }
    var store = options.createStore(options);
    var retryDelay = START_DELAY;
    var flushTimer;
    function shouldQueue(env, error, retryDelay) {
      // We want to drop client reports because they can be generated when we retry sending events while offline.
      if (envelopeContainsItemType(env, ['client_report'])) {
        return false;
      }
      if (options.shouldStore) {
        return options.shouldStore(env, error, retryDelay);
      }
      return true;
    }
    function flushIn(delay) {
      if (flushTimer) {
        clearTimeout(flushTimer);
      }
      flushTimer = setTimeout( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
        var found;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              flushTimer = undefined;
              _context.next = 3;
              return store.shift();
            case 3:
              found = _context.sent;
              if (found) {
                log('Attempting to send previously queued event');

                // We should to update the sent_at timestamp to the current time.
                found[0].sent_at = new Date().toISOString();
                void send(found, true).catch(function (e) {
                  log('Failed to retry sending', e);
                });
              }
            case 5:
            case "end":
              return _context.stop();
          }
        }, _callee);
      })), delay);

      // We need to unref the timer in node.js, otherwise the node process never exit.
      if (typeof flushTimer !== 'number' && flushTimer.unref) {
        flushTimer.unref();
      }
    }
    function flushWithBackOff() {
      if (flushTimer) {
        return;
      }
      flushIn(retryDelay);
      retryDelay = Math.min(retryDelay * 2, MAX_DELAY);
    }
    function send(_x) {
      return _send.apply(this, arguments);
    }
    function _send() {
      _send = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(envelope) {
        var isRetry,
          result,
          delay,
          _args2 = arguments;
        return regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              isRetry = _args2.length > 1 && _args2[1] !== undefined ? _args2[1] : false;
              if (!(!isRetry && envelopeContainsItemType(envelope, ['replay_event', 'replay_recording']))) {
                _context2.next = 6;
                break;
              }
              _context2.next = 4;
              return store.push(envelope);
            case 4:
              flushIn(MIN_DELAY);
              return _context2.abrupt("return", {});
            case 6:
              _context2.prev = 6;
              _context2.next = 9;
              return transport.send(envelope);
            case 9:
              result = _context2.sent;
              delay = MIN_DELAY;
              if (!result) {
                _context2.next = 22;
                break;
              }
              if (!(result.headers && result.headers['retry-after'])) {
                _context2.next = 16;
                break;
              }
              delay = parseRetryAfterHeader(result.headers['retry-after']);
              _context2.next = 22;
              break;
            case 16:
              if (!(result.headers && result.headers['x-sentry-rate-limits'])) {
                _context2.next = 20;
                break;
              }
              delay = 60000; // 60 seconds
              _context2.next = 22;
              break;
            case 20:
              if (!((result.statusCode || 0) >= 400)) {
                _context2.next = 22;
                break;
              }
              return _context2.abrupt("return", result);
            case 22:
              flushIn(delay);
              retryDelay = START_DELAY;
              return _context2.abrupt("return", result);
            case 27:
              _context2.prev = 27;
              _context2.t0 = _context2["catch"](6);
              _context2.next = 31;
              return shouldQueue(envelope, _context2.t0, retryDelay);
            case 31:
              if (!_context2.sent) {
                _context2.next = 44;
                break;
              }
              if (!isRetry) {
                _context2.next = 37;
                break;
              }
              _context2.next = 35;
              return store.unshift(envelope);
            case 35:
              _context2.next = 39;
              break;
            case 37:
              _context2.next = 39;
              return store.push(envelope);
            case 39:
              flushWithBackOff();
              log('Error sending. Event queued.', _context2.t0);
              return _context2.abrupt("return", {});
            case 44:
              throw _context2.t0;
            case 45:
            case "end":
              return _context2.stop();
          }
        }, _callee2, null, [[6, 27]]);
      }));
      return _send.apply(this, arguments);
    }
    if (options.flushAtStartup) {
      flushWithBackOff();
    }
    return {
      send: send,
      flush: function flush(timeout) {
        // If there's no timeout, we should attempt to flush the offline queue.
        if (timeout === undefined) {
          retryDelay = START_DELAY;
          flushIn(MIN_DELAY);
        }
        return transport.flush(timeout);
      }
    };
  };
}
export { MIN_DELAY, START_DELAY, makeOfflineTransport };